import React, { useEffect, useState } from "react";
import { Field, Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import loginStyles from "../../Login/Login.module.scss";
import { useParams, useNavigate } from "react-router-dom";
import { useMask } from "@react-input/mask";
import toast from "react-hot-toast";
import { FiAlertCircle } from "react-icons/fi";
import { HiOutlineLightBulb } from "react-icons/hi";
import PasswordStrength from "components/PasswordStrength/PasswordStrength";
import Avatar from "components/Avatar/Avatar";
import { api, createUserAccount, formatUserData } from "services";
import { LoginLayout } from "pages/Login/Login";
import Button, { ButtonWrapper } from "components/Button/Button";
import { validateCPF } from "utils/validateCPF";
import {
  FormControl,
  FormCustomSelect,
  FormFeedback,
  FormField,
  FormLabel,
  FormRow,
  FormStep,
  FormTerms,
} from "components/Form/Form";
import styles from "./styles.module.scss";

export const checkEmail = async (email) => {
  try {
    return await api.post(`/users/check_email?email=${email}`);
  } catch (error) {
    toast.error(error.response?.data?.error || "Erro ao verificar email");
  }
};

const schoolSituationSchema = Yup.object().shape({
  schoolSituation: Yup.string().required("Campo obrigatório"),
});

const cpfSchema = Yup.object().shape({
  cpf: Yup.string()
    .required("Campo obrigatório")
    .test("cpf", "CPF Inválido", (value) => validateCPF(value)),
});

const nameSchema = Yup.object().shape({
  name: Yup.string().required("Campo obrigatório"),
  cellPhone: Yup.string()
    .required("Campo obrigatório")
    .matches(/^\([1-9]{2}\) [9]{1}[0-9]{4}-[0-9]{4}$/, "Telefone inválido"),
  state: Yup.string().required("Campo obrigatório"),
  municipality: Yup.string().required("Campo obrigatório"),
});

const passwordSchema = Yup.object().shape({
  email: Yup.string().email("E-mail inválido").required("Campo obrigatório"),
  password: Yup.string().required("Campo obrigatório"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Ops! As senhas devem ser iguais")
    .required("Campo obrigatório"),
});

const schoolSchema = Yup.object().shape({
  course: Yup.string().optional(),
  serie: Yup.string().required("Campo obrigatório"),
});

const termsSchema = Yup.object().shape({
  terms: Yup.string().required("É necessário aceitar os termos."),
});

export default function StudentSignUp() {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({});
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);
  const [passwordIsStrong, setPasswordIsStrong] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [municipalityOptions, setMunicipalityOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [findedUser, setFindedUser] = useState(null);
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [selectedSerie, setSelectedSerie] = useState("");
  const [disableCourse, setDisableCourse] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState("");

  const { formStep, connection, link } = useParams();

  useEffect(() => {
    const pathname = window.location.pathname;

    if (pathname.includes("/aluno/")) {
      const newPath = pathname.replace("/aluno", "");
      navigate(newPath);
    }
  }, []);

  const checkUserCpf = async (cpf) => {
    const formatedCpf = cpf.replace(/\D/g, "");
    try {
      const { data } = await api.get(`/users/${formatedCpf}`);
      return data;
    } catch (error) {
      return error.response;
    }
  };

  useEffect(() => {
    if (!formStep) {
      navigate("/criar-uma-conta/consulta-de-dados");
    }
    async function fetchData() {
      async function fetchStates() {
        const { data } = await api.get("/states");
        const states = data.map((state) => {
          return { value: state.id, label: state.name };
        });
        setStateOptions(states);
      }

      try {
        await fetchStates();
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
        toast.error("Erro ao carregar opções de cadastro.");
      }

      async function fetchSeries() {
        const { data } = await api.get("/grades");
        const series = data.connections.map((serie) => {
          return { value: serie.id, label: serie.name };
        });
        setSeriesOptions(series);
      }

      try {
        await Promise.allSettled([fetchStates(), fetchSeries()]);
      } catch (error) {
        console.error("Erro ao carregar dados iniciais:", error);
        toast.error("Erro ao carregar opções de cadastro.");
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    async function fetchMunicipalitiesData() {
      async function fetchMunicipalities() {
        const { data } = await api.get(
          `/states/${selectedState}/municipalities`
        );
        const municipalities = data.map((municipality) => {
          return {
            value: municipality.id,
            label: municipality.name,
          };
        });
        return municipalities;
      }

      if (selectedState) {
        // Continue a verificação condicional
        try {
          const municipalities = await fetchMunicipalities();
          setMunicipalityOptions(municipalities);
        } catch (error) {
          console.error("Erro ao carregar municípios:", error);
          toast.error("Erro ao carregar municípios.");
        }
      } else {
        setMunicipalityOptions([]);
      }
    }

    fetchMunicipalitiesData();
  }, [selectedState]);

  useEffect(() => {
    const fetchCourses = async () => {
      const { data } = await api.get(`/grades/${selectedSerie}/courses`);
      const courses = data.map((course) => {
        return { value: course.id, label: course.title };
      });
      setCourseOptions(courses);

      const integral = courses.find((course) =>
        course.label.toLowerCase().includes("integral")
      );

      if (integral) {
        setSelectedCourse(integral.value);
        setFormValues({
          ...formValues,
          course: integral.value,
          serie: selectedSerie,
        });
        setDisableCourse(true);
      } else {
        setDisableCourse(false);
      }
    };

    if (selectedSerie) {
      fetchCourses();
    } else {
      setCourseOptions([]);
    }
  }, [selectedSerie]);

  const cfpRef = useMask({
    mask: "___.___.___-__",
    replacement: { _: /\d/ },
  });
  const cellPhoneRef = useMask({
    mask: "(__) _____-____",
    replacement: { _: /\d/ },
  });
  const passwordRef = React.createRef();
  const confirmPasswordRef = React.createRef();

  function handleFormValues(values) {
    setFormValues({ ...formValues, ...values });
  }

  const handleNext = async (values, actions) => {
    handleFormValues(values);
    actions.setSubmitting(false);
    setErrors({});

    if (formStep === "consulta-de-dados") {
      const response = await checkUserCpf(values.cpf);

      if (response.status === 409 || response.status === 422) {
        actions.setFieldError("cpf", response.data.error);
        return;
      }
      if (response.status === 404) {
        setFindedUser(null);
      } else {
        setFormValues({
          ...formValues,
          cpf: values.cpf,
          serie: response.connections,
          classroom_ids: response.data.flatMap((inst) =>
            inst.courses.flatMap((course) => course.classrooms.map((c) => c.id))
          ),
        });
        setFindedUser(response);
      }
      navigate(
        link && connection
          ? `/criar-uma-conta/${link}/${connection}/revisar-dados`
          : "/criar-uma-conta/revisar-dados"
      );
    }

    if (formStep === "senha") {
      const email = values.email;
      const response = await checkEmail(email);

      const { found } = response.data;

      if (found) {
        actions.setFieldError(
          "email",
          "Este email já está cadastrado. Termine seu cadastro com um email diferente ou solicite nova senha para este email."
        );
        return;
      }

      if (link && connection) {
        navigate(`/criar-uma-conta/${link}/${connection}/termos`);
      } else if (findedUser) {
        navigate("/criar-uma-conta/termos");
      } else {
        navigate("/criar-uma-conta/situacao-academica");
      }
    }

    if (formStep === "nome") {
      navigate(
        link && connection
          ? `/criar-uma-conta/${link}/${connection}/senha`
          : "/criar-uma-conta/senha"
      );
    }
    if (formStep === "situacao-academica") {
      if (values.schoolSituation === "completed") {
        navigate("/criar-uma-conta/termos");
      } else {
        navigate("/criar-uma-conta/serie");
      }
    }
    if (formStep === "termos") {
      const formatedUser = formatUserData({
        ...formValues,
        name: findedUser ? findedUser.name : formValues.name,
        profile: findedUser ? findedUser.profile : "student",
        ...(link && { link }),
      });

      const response = await createUserAccount(formatedUser);
      if (response) {
        navigate("/criar-uma-conta/conta-criada");
      }
    }
  };

  const handleBackButton = () => {
    if (formStep === "consulta-de-dados")
      return link && connection ? `/login/${link}/${connection}` : "/login";
    if (formStep === "revisar-dados")
      return link && connection
        ? `/criar-uma-conta/${link}/${connection}/consulta-de-dados`
        : "/criar-uma-conta/consulta-de-dados";
    if (formStep === "nome") {
      return link && connection
        ? `/criar-uma-conta/${link}/${connection}/revisar-dados`
        : "/criar-uma-conta/revisar-dados";
    }
    if (formStep === "senha") {
      if (findedUser) {
        return link && connection
          ? `/criar-uma-conta/${link}/${connection}/revisar-dados`
          : "/criar-uma-conta/revisar-dados";
      } else {
        return link && connection
          ? `/criar-uma-conta/${link}/${connection}/nome`
          : "/criar-uma-conta/nome";
      }
    }
    if (formStep === "situacao-academica") return "/criar-uma-conta/senha";
    if (formStep === "serie") return "/criar-uma-conta/situacao-academica";

    if (formStep === "termos") {
      if (link && connection) {
        return `/criar-uma-conta/${link}/${connection}/senha`;
      }
      if (findedUser) return "/criar-uma-conta/senha";
      if (formValues?.schoolSituation === "completed")
        return "/criar-uma-conta/situacao-academica";
      if (formValues.schoolSituation === "not_enrolled")
        return "/criar-uma-conta/serie";
    }
  };

  const academicList =
    findedUser &&
    findedUser?.data?.flatMap((institution) =>
      institution?.courses?.map((course) => ({
        institution: institution?.institution,
        courseTitle: course?.title,
        classrooms: course?.classrooms?.map((c) => c?.title).join(", "),
      }))
    );

  return (
    <LoginLayout back={handleBackButton()}>
      <FormStep show={formStep === "consulta-de-dados"}>
        <Formik
          initialValues={{ cpf: formValues.cpf ?? "" }}
          validationSchema={cpfSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <div className={loginStyles.textGroup}>
                  <h1 className={loginStyles.title}>
                    Vamos consultar seu dados na <strong>Rede SEDUC</strong>
                  </h1>
                </div>

                <FormRow>
                  <FormLabel htmlFor="cpf">Digite seu CPF</FormLabel>
                  <FormControl
                    id="cpf"
                    name="cpf"
                    type="text"
                    value={props.values.cpf}
                    invalid={props.errors.cpf && props.touched.cpf}
                    placeholder="000.000.000-00"
                    ref={cfpRef}
                  />
                  <ErrorMessage name="cpf">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={props.isSubmitting || !props.values.cpf}
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>
      <FormStep show={formStep === "revisar-dados"}>
        <Formik>
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <div className={loginStyles.textGroup}>
                  <h1 className={loginStyles.title}>
                    {findedUser ? (
                      <p className="text-primary-dark">
                        Esses foram os dados encontrados relativos ao{" "}
                        <strong>CPF {formValues.cpf}</strong>. Selecione o que
                        mais se aproxima da sua realidade
                      </p>
                    ) : (
                      <p className="text-primary-dark">
                        Que pena! Seus dados{" "}
                        <strong>não foram encontrados no iSeduc.</strong>
                      </p>
                    )}
                  </h1>
                </div>

                <FormRow>
                  <div
                    className={styles["user-card"]}
                    style={{
                      background: "linear-gradient(#f9fcfd 0%, #ecf2f7 100%)",
                    }}
                  >
                    <div className={styles["user-avatar"]}>
                      {findedUser ? (
                        <Avatar
                          className={`avatar ${styles["user-avatar--img"]}`}
                          src={null}
                        />
                      ) : (
                        <div
                          className={`avatar ${styles["user-avatar--img"]} ${styles["user-avatar--not-finded"]}`}
                        >
                          ?
                        </div>
                      )}
                      {findedUser && (
                        <span className={` ${styles["user-avatar--badge"]}`}>
                          {findedUser?.profile === "student"
                            ? "Aluno"
                            : "Professor"}
                        </span>
                      )}
                    </div>
                    <div className={styles["user-data"]}>
                      <p className={styles["user-data-name"]}>
                        {findedUser
                          ? findedUser.name
                          : "Usuário não encontrado"}
                      </p>
                      <p
                        className={`mb-0 ${
                          findedUser
                            ? styles["user-data-text--disabled"]
                            : styles["user-data-error"]
                        }`}
                      >
                        CPF: {findedUser ? findedUser.cpf : "não encontrado"}
                      </p>
                      {findedUser && findedUser.email && (
                        <p className={styles["user-data-text--disabled"]}>
                          Email: {findedUser.email}
                        </p>
                      )}

                      <div className={styles["user-academic-data"]}>
                        {findedUser &&
                          findedUser.data &&
                          academicList.map((item, index) => (
                            <div style={{ marginBottom: "1.5rem" }} key={index}>
                              <p>
                                <span style={{ fontWeight: "bold" }}>
                                  Escola:
                                </span>{" "}
                                {item.institution}
                              </p>
                              <p>
                                <span style={{ fontWeight: "bold" }}>
                                  Curso:
                                </span>{" "}
                                {item.courseTitle}
                              </p>
                              <p>
                                <span style={{ fontWeight: "bold" }}>
                                  Turma:
                                </span>{" "}
                                {item.classrooms}
                              </p>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                  <div className={styles["info"]}>
                    <span
                      className={`${styles["alert-icon"]} ${styles["alert-icon--warning"]}`}
                    >
                      <FiAlertCircle />
                    </span>
                    <div>
                      <p className="mb-0">
                        {findedUser ? (
                          <>
                            <b>Atenção: </b>caso você discorde, atualize seus
                            dados junto à gestão escolar. Para continuar
                            acessando os conteúdos de 2025, é só pedir para a
                            gestão da sua escola atualizar seus dados.
                          </>
                        ) : (
                          "Clique no botão abaixo para fazer seu cadastro e ter acesso aos conteúdos"
                        )}
                      </p>
                    </div>
                  </div>
                </FormRow>
              </div>

              <ButtonWrapper>
                {findedUser ? (
                  <>
                    <Button
                      modifiers={["success"]}
                      anchor
                      to={
                        link && connection
                          ? `/criar-uma-conta/${link}/${connection}/senha`
                          : "/criar-uma-conta/senha"
                      }
                    >
                      Sim. Vamos seguir
                    </Button>
                    <Button
                      modifiers={["danger"]}
                      anchor
                      to={"/criar-uma-conta/fim"}
                    >
                      Não. Preciso corrigir
                    </Button>
                  </>
                ) : (
                  <Button
                    modifiers={["outline"]}
                    anchor
                    to={
                      link && connection
                        ? `/criar-uma-conta/${link}/${connection}/nome`
                        : "/criar-uma-conta/nome"
                    }
                    style={{ minWidth: 175 }}
                  >
                    Quero fazer meu cadastro
                  </Button>
                )}
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === "nome"}>
        <Formik
          initialValues={{
            name: formValues.name,
            cellPhone: formValues.cellPhone ?? "",
            state: formValues.state ?? "",
            municipality: formValues.municipality ?? "",
          }}
          validationSchema={nameSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <div className={loginStyles.textGroup}>
                  <h1 className={loginStyles.title}>
                    <strong>Certo, vamos fazer o seu cadastro</strong>
                    <p>
                      Informe seu nome completo, telefone, estado e município
                    </p>
                  </h1>
                </div>

                <FormRow>
                  <FormLabel htmlFor="name">*Nome completo</FormLabel>
                  <FormControl
                    id="name"
                    name="name"
                    type="text"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                    value={props.values.name}
                    placeholder="Insira seu nome completo"
                  />
                </FormRow>
                <div className={styles["flex-fields"]}>
                  <FormRow>
                    <FormLabel htmlFor="cellPhone">Telefone com DDD</FormLabel>
                    <FormControl
                      id="cellPhone"
                      name="cellPhone"
                      type="tel"
                      value={props.values.cellPhone}
                      invalid={
                        props.errors.cellPhone && props.touched.cellPhone
                      }
                      placeholder="(00) 00000-0000"
                      ref={cellPhoneRef}
                    />
                    <ErrorMessage name="cellPhone">
                      {(msg) => (
                        <FormFeedback invalid={true}>{msg}</FormFeedback>
                      )}
                    </ErrorMessage>
                  </FormRow>
                  <FormRow>
                    <FormLabel htmlFor="cpf">*CPF</FormLabel>
                    <FormControl
                      id="cpf"
                      name="cpf"
                      type="text"
                      value={formValues.cpf}
                      placeholder="000.000.000-00"
                      ref={cfpRef}
                      disabled
                    />
                  </FormRow>
                </div>
                <div className={styles["flex-fields"]}>
                  <FormRow className={styles["flex-fields-item"]}>
                    <FormLabel>*Estado</FormLabel>
                    <FormCustomSelect
                      values={props.values}
                      name="state"
                      options={stateOptions}
                      onChange={setSelectedState}
                      placeholder="Pesquise por estado"
                    />
                  </FormRow>

                  <FormRow
                    className={styles["flex-fields-item"]}
                    disabled={!selectedState}
                  >
                    <FormLabel>*Município</FormLabel>
                    <FormCustomSelect
                      values={props.values}
                      name="municipality"
                      options={municipalityOptions}
                      placeholder="Pesquise por município"
                    />
                  </FormRow>
                </div>
              </div>
              <ButtonWrapper>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={
                    props.isSubmitting ||
                    !props.values.name ||
                    !props.values.cellPhone ||
                    !props.values.state ||
                    !props.values.municipality
                  }
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>
      <FormStep show={formStep === "senha"}>
        <Formik
          initialValues={{
            email: formValues.email,
            password: formValues.password ?? "",
            confirmPassword: formValues.confirmPassword ?? "",
          }}
          validationSchema={passwordSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <div className={loginStyles.textGroup}>
                  <h1 className={loginStyles.title}>
                    <b>Muito bom!</b> Agora, você precisa de email e senha para
                    acessar a plataforma. Informe abaixo:
                  </h1>
                </div>

                <FormRow>
                  <FormLabel htmlFor="email">*Email</FormLabel>
                  <FormControl
                    id="email"
                    name="email"
                    type="email"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                    value={props.values.email}
                    invalid={props.errors.email && props.touched.email}
                    placeholder="Insira seu e-mail"
                  />
                  <ErrorMessage name="email">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                  {errors?.email && (
                    <ErrorMessage name="email">
                      {
                        <FormFeedback invalid={true}>
                          {errors.email}
                        </FormFeedback>
                      }
                    </ErrorMessage>
                  )}
                </FormRow>
                <FormRow>
                  <FormLabel htmlFor="password">*Senha</FormLabel>
                  <PasswordStrength
                    show={showPasswordStrength}
                    password={props.values.password}
                    validPassword={setPasswordIsStrong}
                  >
                    <FormControl
                      id="password"
                      name="password"
                      type={props.values.showPassword ? "text" : "password"}
                      onChange={props.handleChange}
                      onFocus={() => {
                        setShowPasswordStrength(true);
                        passwordRef.current.removeAttribute("readOnly");
                      }}
                      onBlur={() => {
                        setShowPasswordStrength(false);
                        passwordRef.current.setAttribute("readOnly", "");
                      }}
                      value={props.values.password}
                      invalid={props.errors.password && props.touched.password}
                      placeholder="Insira sua senha"
                      ref={passwordRef}
                      autoComplete="off"
                      readOnly
                    />
                  </PasswordStrength>
                  <FormField>
                    <Field type="checkbox" name="showPassword" />
                    Mostrar senha
                  </FormField>
                  <ErrorMessage name="password">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                </FormRow>

                <FormRow>
                  <FormLabel htmlFor="confirmPassword">
                    *Confirme sua senha
                  </FormLabel>
                  <FormControl
                    id="confirmPassword"
                    name="confirmPassword"
                    type={
                      props.values.showConfirmPassword ? "text" : "password"
                    }
                    onChange={props.handleChange}
                    onFocus={() =>
                      confirmPasswordRef.current.removeAttribute("readOnly")
                    }
                    onBlur={() =>
                      confirmPasswordRef.current.setAttribute("readOnly", "")
                    }
                    value={props.values.confirmPassword}
                    invalid={
                      props.errors.confirmPassword &&
                      props.touched.confirmPassword
                    }
                    placeholder="Insira sua senha"
                    ref={confirmPasswordRef}
                    autoComplete="off"
                    readOnly
                  />
                  <FormField>
                    <Field type="checkbox" name="showConfirmPassword" />
                    Mostrar senha
                  </FormField>
                  <ErrorMessage name="confirmPassword">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                </FormRow>
              </div>
              <div
                className={`${styles["email-info"]} ${loginStyles.box} ${loginStyles.description}`}
              >
                <HiOutlineLightBulb
                  className={`${styles["email-info-icon"]}`}
                />

                <p className="mb-0">
                  Toda a comunicação da plataforma será feita pelo email que
                  você cadastrar. Anote-o para não esquecer.
                </p>
              </div>
              <ButtonWrapper>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={
                    props.isSubmitting ||
                    !props.values.email ||
                    !props.values.password ||
                    !props.values.confirmPassword ||
                    !passwordIsStrong
                  }
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === "situacao-academica"}>
        <Formik
          initialValues={{ schoolSituation: formValues.schoolSituation ?? "" }}
          validationSchema={schoolSituationSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <h1 className={loginStyles.title} id="my-radio-group">
                  <strong>Estamos quase acabando...</strong>
                  <p className="mb-0">
                    Precisamos saber sua situação escolar. Escolha uma das
                    alternativas abaixo:
                  </p>
                </h1>

                <FormRow
                  role="group"
                  aria-labelledby="my-radio-group"
                  style={{ gap: 24 }}
                >
                  <FormField>
                    <Field
                      type="radio"
                      name="schoolSituation"
                      value="not_enrolled"
                    />
                    Quero estudar mas não estou matriculado em nenhuma escola
                  </FormField>
                  <FormField>
                    <Field
                      type="radio"
                      name="schoolSituation"
                      value="completed"
                    />
                    Já conclui o Ensino Médio e quero revisar matérias
                  </FormField>
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={props.isSubmitting || !props.values.schoolSituation}
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === "serie"}>
        <Formik
          initialValues={{
            serie: formValues.serie ?? "",
            course: formValues.course ?? "",
          }}
          validationSchema={schoolSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <div className={loginStyles.textGroup}>
                  <h1 className={loginStyles.title}>
                    <strong>Selecione o conteúdo que você quer estudar</strong>
                    <p>Escolha a série e o curso</p>
                  </h1>
                </div>

                <FormRow>
                  <FormLabel>Série</FormLabel>
                  <FormCustomSelect
                    values={props.values}
                    name="serie"
                    options={seriesOptions}
                    onChange={setSelectedSerie}
                    placeholder="Selecione a série"
                  />
                </FormRow>

                <FormRow disabled={!props.values.serie || disableCourse}>
                  <FormLabel>Curso</FormLabel>
                  <FormCustomSelect
                    values={props.values}
                    name="course"
                    options={courseOptions}
                    onChange={setSelectedCourse}
                    placeholder={
                      disableCourse
                        ? courseOptions.find(
                            (item) => item.value === selectedCourse
                          )?.label
                        : "Selecione o curso"
                    }
                  />
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  onClick={() => {
                    navigate("/criar-uma-conta/termos");
                  }}
                  style={{ minWidth: 134 }}
                  disabled={
                    props.isSubmitting || disableCourse
                      ? !props.values.serie
                      : !props.values.serie ||
                        (courseOptions.length > 0 && !props.values.course)
                  }
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === "termos"}>
        <Formik
          initialValues={{ terms: formValues.terms ?? true }}
          validationSchema={termsSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <div className={loginStyles.textGroup}>
                  <h1 className={loginStyles.title}>
                    <strong>Por fim,</strong> nosso termos de uso e política de
                    privacidade
                  </h1>
                </div>

                <FormRow>
                  <FormTerms />
                </FormRow>

                <FormRow>
                  <FormField round>
                    <Field type="checkbox" name="terms" />
                    <span>
                      Ao clicar em “Criar conta”, você concorda com os{" "}
                      <a
                        href="/termos-de-uso"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        termos de uso
                      </a>{" "}
                      e{" "}
                      <a
                        href="/politica-de-privacidade"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        política de privacidade
                      </a>
                    </span>
                  </FormField>
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={!props.values.terms}
                >
                  Criar minha conta
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === "conta-criada"}>
        <Formik>
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <FormRow>
                  <strong style={{ fontWeight: "bold" }}>
                    Tudo certo. Sua conta foi criada!
                  </strong>
                  <p>
                    Enviamos um e-mail para você. Acesse-o para ativar sua conta
                  </p>

                  <strong
                    style={{
                      fontWeight: "bold",
                      marginTop: 24,
                      color: "var(--color-primary)",
                    }}
                  >
                    É muito bom ter você com a gente!
                  </strong>
                </FormRow>
              </div>
            </Form>
          )}
        </Formik>
      </FormStep>
      <FormStep show={formStep === "fim"}>
        <Formik>
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <FormRow>
                  <p style={{ fontWeight: "bold" }}>Tudo bem!</p>
                  <p>
                    Vá até a sua escola para corrigir seus dados. Depois disso,
                    é só voltar aqui e finalizar seu cadastro para ter acesso
                    aos conteúdos e começar a estudar!
                  </p>
                  <p style={{ marginTop: 12 }}>Estaremos te esperando.</p>
                </FormRow>
              </div>
            </Form>
          )}
        </Formik>
      </FormStep>
    </LoginLayout>
  );
}
